import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PsychologyIcon from '@mui/icons-material/Psychology';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { InlineWidget } from 'react-calendly';

import SEO from '../components/SEO';
import { trackScheduleCall } from '../components/GoogleAnalytics';
import { generateWebPageSchema } from '../utils/schemaGenerators';

// Principle II — Single Source of Truth: Calendly URL defined exactly once
const CALENDLY_URL = 'https://calendly.com/robert-garza-us';

const whatToDiscuss = [
  ['Custody arrangements and parenting time', 'Case strategy and next steps'],
  ['Child support disputes and calculations', 'False allegations and protective orders'],
  ['Parental rights and due process concerns', 'Parental alienation situations'],
  ['Court preparation and what to expect', 'How to engage your attorney more effectively'],
];

const ScheduleConsultation: React.FC = () => {
  // data-model.md: Loading / Loaded / Failed states — Principle VII
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);
  const calendlyRef = useRef<HTMLDivElement>(null);

  const scrollToCalendly = () => {
    calendlyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      // Mark widget as loaded when Calendly signals its height (first render event)
      if (e.data?.event === 'calendly.page_height') {
        setCalendlyLoaded(true);
      }
      // FR-010: fire tracking when visitor completes a booking
      if (e.data?.event === 'calendly.event_scheduled') {
        trackScheduleCall('ConsultationPage');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const schema = generateWebPageSchema({
    name: 'Schedule a Consultation - RobertGarza.us',
    description:
      'Book a private one-on-one strategy call with Robert Garza to discuss your family law case and formulate a plan. This is a strategic opinion, not legal advice.',
    url: '/schedule-consultation',
  });

  return (
    <>
      {/* FR-009: SEO metadata */}
      <SEO
        title="Schedule a Consultation - RobertGarza.us"
        description="Book a private one-on-one strategy call with Robert Garza to discuss your family law case and formulate a plan. This is a strategic opinion, not legal advice."
        path="/schedule-consultation"
        schema={schema}
      />

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* FR-003 / US2 T007: Hero section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography
            component="h1"
            variant="h3"
            sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2rem', md: '2.75rem' } }}
          >
            Book a Private Strategy Call
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '720px', mx: 'auto', fontWeight: 400 }}>
            Get one-on-one time with Robert Garza to walk through the specific details of your family law case and build
            a clear, informed strategy. Private, confidential, and tailored to your situation.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={scrollToCalendly}
            sx={{
              mt: 4,
              px: 5,
              py: 1.75,
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: '9999px',
              textTransform: 'none',
            }}
          >
            Book My Strategy Call
          </Button>
        </Box>

        {/* US2 T008: "What to Expect" 3-column cards (plan-driven) */}
        <Grid container spacing={3} sx={{ mb: { xs: 4, md: 6 } }}>
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%', textAlign: 'center', p: 1 }}>
              <CardContent>
                <LockIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Private & Confidential
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Every conversation is between you and Robert only. What you share stays private, giving you the
                  freedom to speak openly about your case.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%', textAlign: 'center', p: 1 }}>
              <CardContent>
                <PsychologyIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Opinion-Based Strategy
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This is not legal advice. Robert will share an informed strategic opinion to help you think through
                  your options and formulate your own approach.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%', textAlign: 'center', p: 1 }}>
              <CardContent>
                <PersonIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Tailored to Your Case
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No generic answers. Robert listens to the specifics of your situation and provides a perspective
                  relevant to your unique circumstances.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* FR-005 / US2 T009: What We'll Discuss */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            What We'll Discuss
          </Typography>
          <Grid container>
            {[0, 1].map((col) => (
              <Grid item xs={12} md={6} key={col}>
                <List dense>
                  {whatToDiscuss.map((pair) => (
                    <ListItem key={pair[col]} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={pair[col]} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FR-004 / US2 T010: Disclaimer — SC-005: clearly visible above embed */}
        <Alert severity="info" sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography variant="body2">
            <strong>Important:</strong> This consultation is not legal advice. Robert Garza will provide a strategic
            opinion to help you formulate your approach to your family law matter. For legal representation, please
            consult a licensed attorney in your state.
          </Typography>
        </Alert>

        {/* FR-002 / US1 T003: Calendly inline embed with Loading / Loaded states */}
        <Box ref={calendlyRef} sx={{ position: 'relative', minHeight: '1000px' }}>
          {/* Loading state: visible until Calendly iframe signals readiness */}
          {!calendlyLoaded && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '1000px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <CircularProgress aria-label="Loading scheduling widget" />
            </Box>
          )}
          <InlineWidget url={CALENDLY_URL} styles={{ minHeight: '1000px', width: '100%' }} />
        </Box>

        {/* Constitution III — Fail Loud, Never Silent: always-visible fallback T006 */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Having trouble?{' '}
            <Box
              component="a"
              href="mailto:info@robertgarza.us"
              sx={{ color: 'primary.main', textDecoration: 'underline' }}
            >
              Email info@robertgarza.us
            </Box>{' '}
            to schedule directly.
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default ScheduleConsultation;
